import React, { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { sendAadhaarOTP } from "@/api/user/sendAadhaarOTP";
import { validateAadhaarOTP } from "@/api/user/validateAadhaarOTP";
import { useTranslations } from "next-intl";
import Toast from "@/common/toast.common";

const OTPModal = ({ aadhar_number, onClose = () => {}, onVerifySuccess = () => {}, }) => {
  const t = useTranslations();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [mobileNumber, setMobileNumber] = useState("");
  const [referenceId, setReferenceId] = useState("");
  const [hasInitialOtpSent, setHasInitialOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const inputRefs = useRef([]);
  const hasSentOtpRef = useRef(false);

  const [toastState, setToastState] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") => {
    setToastState({ open: true, message, type });
  };

  const closeToast = () => {
    setToastState((prev) => ({ ...prev, open: false }));
  };

  const sendOtpMutation = useMutation({
    mutationFn: () => sendAadhaarOTP(aadhar_number.replace(/[^\d]/g, "")),
    onSuccess: (data) => {
      if (data && data.reference_id) {
        setReferenceId(data.reference_id);
      }
      setHasInitialOtpSent(true);
      showToast("OTP sent successfully!", "success");
    },
    onError: (error) => {
      setHasInitialOtpSent(true);
      showToast(error || "Failed to send OTP.", "error");
      setTimeOut(() => (
        onClose(true)
      ), 3000)
    },
  });

  const validateOtpMutation = useMutation({
    mutationFn: ({ reference_id, mobile_number, otp }) =>
      validateAadhaarOTP(reference_id, mobile_number, otp),
    onSuccess: (data) => {
      showToast("OTP verified successfully!", "success");
      onVerifySuccess(data);
    },
    onError: (error) => {
      showToast(error || "OTP verification failed. Please try again.", "error");
    },
  });

  useEffect(() => {
    if (hasInitialOtpSent && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [hasInitialOtpSent]);

  useEffect(() => {
    if (!hasSentOtpRef.current) {
      sendOtpMutation.mutate();
      hasSentOtpRef.current = true;
    }
  }, []);

  const handleInputChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  useEffect(() => {
    let timer;
    if (resendTimer > 0 && hasInitialOtpSent) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer, hasInitialOtpSent]);

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    const newOtp = [...otp];
    for (let i = 0; i < pastedData.length; i++) {
      newOtp[i] = pastedData[i];
    }
    setOtp(newOtp);
    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleMobileNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setMobileNumber(value);
  };

  const handleSubmit = async () => {
    const otpValue = otp.join("");

    if (!mobileNumber || mobileNumber.length !== 10) {
      showToast("Please enter a valid 10-digit mobile number", "warning");
      return;
    }

    if (otpValue.length !== 6) {
      showToast("Please enter complete 6-digit OTP", "warning");
      return;
    }

    if (!referenceId) {
      showToast("Reference ID not found. Please try resending OTP.", "error");
      return;
    }

    validateOtpMutation.mutate({
      reference_id: referenceId,
      mobile_number: mobileNumber,
      otp: otpValue,
    });
  };

  const handleResendOtp = () => {
    if (!sendOtpMutation.isPending && resendTimer === 0) {
      sendOtpMutation.mutate();
      setResendTimer(60);
    }
  };

  const isFormComplete =
    mobileNumber.length === 10 && otp.every((digit) => digit !== "");

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-auto">
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">{t("Verify OTP")}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none hover:cursor-pointer"
            >
              ×
            </button>
          </div>

          <div className="p-6">
            {!hasInitialOtpSent ? (
              // Loading state while sending initial OTP
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mb-4"></div>
                <p className="text-gray-600 mb-2">{t("Sending OTP to Aadhaar number")}</p>
                <p className="font-mono text-lg text-green-600">{aadhar_number}</p>
                <p className="text-sm text-gray-500 mt-2">{t("Please wait...")}</p>
              </div>
            ) : (
              // Form content after OTP is sent
              <>
                <div className="text-center mb-6">
                  <p className="text-gray-600 mb-2">
                    {t("EnterMobAndOTP")}
                  </p>
                  <p className="font-mono text-lg text-green-600">
                    {aadhar_number}
                  </p>
                </div>

                {/* Mobile Number Field */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("Mobile Number")}
                  </label>
                  <input
                    type="tel"
                    value={mobileNumber}
                    onChange={handleMobileNumberChange}
                    placeholder={t("Enter 10-digit mobile number")}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none transition-colors text-center font-mono text-lg"
                    maxLength={10}
                    inputMode="numeric"
                  />
                </div>

                {/* OTP Input Fields */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
                    {t("Enter OTP")}
                  </label>
                  <div className="flex justify-center gap-3">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (inputRefs.current[index] = el)}
                        type="text"
                        value={digit}
                        onChange={(e) => handleInputChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={handlePaste}
                        className="w-12 h-12 text-center text-xl font-semibold border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none transition-colors"
                        maxLength={1}
                        inputMode="numeric"
                      />
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={!isFormComplete || validateOtpMutation.isPending}
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                    isFormComplete && !validateOtpMutation.isPending
                      ? "bg-green-600 hover:bg-green-700 text-white hover:cursor-pointer"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  {validateOtpMutation.isPending ? t("VerifyinGG") : t("Verify OTP")}
                </button>

                <div className="text-center mt-4">
                  <p className="text-sm text-gray-600">
                    {t("Didn't receive OTP")}{" "}
                    {resendTimer > 0 ? (
                      <span className="text-gray-400">
                        {t("Resend in")} {resendTimer}s
                      </span>
                    ) : (
                      <button
                        className={`font-medium text-green-600 hover:text-green-700 ${
                          sendOtpMutation.isPending
                            ? "cursor-not-allowed opacity-50"
                            : ""
                        }`}
                        onClick={handleResendOtp}
                        disabled={sendOtpMutation.isPending || resendTimer > 0}
                      >
                        {sendOtpMutation.isPending ? t("Sending...") : t("Resend OTP")}
                      </button>
                    )}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Toast Handler */}
      <Toast
        open={toastState.open}
        onClose={closeToast}
        message={toastState.message}
        type={toastState.type}
      />
    </>
  );
};

export default OTPModal;